import { useEffect, useRef, useState, type FormEvent } from 'react'
import { toast } from '../components/toasts'
import { needsServer } from '../components/needsServer'
import { useUnsavedChanges } from '../components/unsaved'
import { useWorkspaceDraft } from '../workspace/drafts'
import { useBoardState, useStore } from '../store/context'
import type { Label } from '../api/types'
import type { LabelOutcome } from '../store/store'
import { settingsWords } from './words'
import { useSettingsProject } from './selection'

const words = settingsWords.labels

/**
 * The labels an issue can carry: one row each, its colour, its name and its
 * Remove button. A row is saved as she changes it: the name as she leaves the
 * field or presses Return, the colour once she has picked it. No Save button.
 * The last row adds a label.
 */
export function LabelSettings() {
  const { labels } = useBoardState()
  const project = useSettingsProject()
  if (!project) return null
  return <section className="settings-section">
    <ul className="settings-rows settings-labels">
      {[...labels.values()].filter(label => label.project === project.id && !label.deleted_at).sort((a, b) => a.name.localeCompare(b.name)).map(label => <LabelRow key={label.id} label={label} />)}
      <NewLabel key={project.id} projectId={project.id} />
    </ul>
  </section>
}

/**
 * A colour well. The browser says `input` at each colour she passes over and
 * `change` once she has picked: `onPick` follows the first, to show it, and
 * `onPicked` the second, which is the one to save.
 */
function Colour({ label, value, onPick, onPicked }: { label: string; value: string; onPick: (color: string) => void; onPicked?: (color: string) => void }) {
  const input = useRef<HTMLInputElement>(null)
  const picked = useRef(onPicked)
  useEffect(() => { picked.current = onPicked })
  useEffect(() => {
    const element = input.current
    if (!element) return
    const done = () => picked.current?.(element.value)
    element.addEventListener('change', done)
    return () => element.removeEventListener('change', done)
  }, [])
  return <input ref={input} type="color" aria-label={label} value={value} onChange={event => onPick(event.target.value)} />
}

type Draft = Pick<Label, 'name' | 'colour'>

function LabelRow({ label }: { label: Label }) {
  const store = useStore()
  const [draft, setDraft] = useState<Draft>({ name: label.name, colour: label.colour })
  const [removing, setRemoving] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  // What a save sends is the row as it stands at that moment, and saves go one after the other:
  // a colour picked while the name is being saved overwrites nothing.
  const latest = useRef(draft)
  const saves = useRef<Promise<void>>(Promise.resolve())
  const edit = (change: Partial<Draft>) => { latest.current = { ...latest.current, ...change }; setDraft(latest.current) }
  // A field follows what is saved when that changes under it, and only then.
  const failedDraft = useRef<Draft | null>(null)
  const held = useRef(label)
  useEffect(() => {
    const before = held.current
    held.current = label
    if (failedDraft.current) { edit(failedDraft.current); failedDraft.current = null; return }
    const moved: Partial<Draft> = {}
    if (before.name !== label.name && (latest.current.name === before.name || latest.current.name.trim() === label.name)) moved.name = label.name
    if (before.colour !== label.colour && latest.current.colour === before.colour) moved.colour = label.colour
    if (Object.keys(moved).length) edit(moved)
  }, [label, problem])
  const suffix = `: ${label.name}`

  // The row as it was last sent: Return and then leaving the field is one save, not two.
  const sent = useRef('')

  function send(work: () => Promise<LabelOutcome>, done?: () => void) {
    const owner = store.getState().me
    const current = () => owner !== null && store.getState().me === owner
    const attempted = latest.current
    setBusy(true)
    setProblem(null)
    saves.current = saves.current.then(async () => {
      if (!current()) return
      try {
        const outcome = await work()
        if (!current()) return
        if (!outcome.ok) throw new Error(outcome.message)
        done?.()
      } catch (error) {
        if (!current()) return
        sent.current = ''
        failedDraft.current = { name: latest.current.name === label.name ? attempted.name : latest.current.name, colour: latest.current.colour === label.colour ? attempted.colour : latest.current.colour }
        setProblem(error instanceof Error ? error.message : words.failed)
      } finally { setBusy(false) }
    })
  }
  function save() {
    const { name, colour } = latest.current
    const next = name.trim()
    if (!next) { edit({ name: label.name }); return }
    const row = JSON.stringify([next, colour])
    if (row === sent.current || row === JSON.stringify([held.current.name, held.current.colour])) return
    sent.current = row
    send(() => store.updateLabel(label.id, { name: next, colour }), () => toast(settingsWords.saved))
  }
  return <li>
    <form className="settings-row-line" onSubmit={(event: FormEvent) => { event.preventDefault(); save() }}>
      <Colour label={words.colour + suffix} value={draft.colour} onPick={colour => edit({ colour })} onPicked={colour => { edit({ colour }); save() }} />
      <input className="settings-label-name" aria-label={words.name + suffix} value={draft.name} maxLength={60} autoComplete="off"
        onChange={event => edit({ name: event.target.value })} onBlur={save}
        onKeyDown={event => { if (event.key === 'Escape' && draft.name !== label.name) { event.preventDefault(); edit({ name: label.name }); setProblem(null) } }} />
      <button type="button" className="button button-quiet" aria-label={`${words.remove} ${label.name}`} disabled={removing} onClick={() => setRemoving(true)}>{words.remove}</button>
    </form>
    {removing && <div className="settings-accept-band"><p>{words.asks(label.name)}</p>
      <div className="form-actions">
        <button type="button" className="button" disabled={busy} onClick={() => setRemoving(false)}>{words.keep}</button>
        <button type="button" className="button" disabled={busy} onClick={() => send(() => store.deleteLabel(label.id))}>{words.removeIt}</button>
      </div>
    </div>}
    {problem && <p role="alert" className="text-danger">{problem}</p>}
  </li>
}

function NewLabel({ projectId }: { projectId: string }) {
  const store = useStore()
  const { offline } = useBoardState()
  const [name, setName] = useWorkspaceDraft('label.new.name', '')
  const [color, setColor] = useWorkspaceDraft('label.new.colour', '#808080', undefined, () => false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  useUnsavedChanges(name !== '' && !busy)
  async function add(event: FormEvent) {
    event.preventDefault()
    const next = name.trim()
    if (!next || busy || store.getState().offline) return
    setBusy(true); setProblem(null)
    try {
      const outcome = await store.createLabel(projectId, next, color)
      if (!outcome.ok) throw new Error(outcome.message)
      setName('')
    } catch (error) { setProblem(error instanceof Error ? error.message : words.failed) }
    finally { setBusy(false) }
  }
  return <li>
    <form className="settings-row-line" onSubmit={event => void add(event)}>
      <Colour label={words.colour} value={color} onPick={setColor} />
      <input className="settings-label-name" aria-label={words.name} placeholder={words.newLabel} value={name} maxLength={60} readOnly={busy} autoComplete="off"
        onChange={event => { setName(event.target.value); setProblem(null) }} />
      <button className="button" disabled={busy || offline || !name.trim()} {...needsServer(offline)}>{words.add}</button>
    </form>
    {problem && <p role="alert" className="text-danger">{problem}</p>}
  </li>
}
